import { ref, onUnmounted, nextTick, type Ref } from 'vue';

export interface ContainerResizeState {
  baseSize: Ref<{ width: number; height: number }>;
  containerSize: Ref<{ width: number; height: number }>;
  scale: Ref<{ x: number; y: number }>;
}

/**
 * Composable для отслеживания размера контейнера и вычисления масштаба
 * Используется для масштабирования координат при изменении размера контейнера
 * 
 * @param containerRef - Ref на DOM элемент контейнера
 * @param onBaseSizeInitialized - Колбэк при инициализации базового размера
 * @param onScaleUpdated - Колбэк при обновлении масштаба
 * @returns Объект с reactive размерами и масштабом
 */
export function useContainerResize(
  containerRef: Ref<HTMLElement | null>,
  options: {
    onBaseSizeInitialized?: (size: { width: number; height: number }) => void;
    onScaleUpdated?: (scale: { x: number; y: number }, containerSize: { width: number; height: number }) => void;
  } = {}
): ContainerResizeState {
  const baseSize = ref({ width: 0, height: 0 });
  const containerSize = ref({ width: 0, height: 0 });
  const scale = ref({ x: 1, y: 1 });

  let resizeObserver: ResizeObserver | null = null;
  let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastSize = { width: 0, height: 0 };
  let stableSizeCount = 0;
  let maxSize = { width: 0, height: 0 }; // Отслеживаем максимальный размер
  const STABLE_SIZE_THRESHOLD = 5; // Количество одинаковых размеров подряд для стабилизации
  const STABLE_DELAY = 300; // Задержка в мс для установки базового размера после стабилизации

  // Функция для установки базового размера
  const initializeBaseSize = () => {
    if (containerRef.value) {
      const rect = containerRef.value.getBoundingClientRect();
      const initialWidth = rect.width;
      const initialHeight = rect.height;
      
      // Устанавливаем базовый размер только если контейнер имеет реальный размер
      if (initialWidth > 0 && initialHeight > 0) {
        // Если baseSize еще не установлен, устанавливаем его
        if (baseSize.value.width === 0 || baseSize.value.height === 0) {
          baseSize.value = { width: initialWidth, height: initialHeight };
          containerSize.value = { width: initialWidth, height: initialHeight };
          scale.value = { x: 1, y: 1 };
          
          if (options.onBaseSizeInitialized) {
            options.onBaseSizeInitialized({ width: initialWidth, height: initialHeight });
          }
        }
      }
    }
  };

  // Инициализация ResizeObserver
  const setupResizeObserver = () => {
    if (!containerRef.value) return;

    // Создаем ResizeObserver для отслеживания изменений размера
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          containerSize.value = { width, height };

          // Если baseSize еще не установлен, ждем стабилизации размера
          if (baseSize.value.width === 0 || baseSize.value.height === 0) {
            // Отслеживаем максимальный размер (на случай, если контейнер увеличивается)
            const isLarger = width > maxSize.width || height > maxSize.height;
            if (isLarger) {
              maxSize = { width, height };
              stableSizeCount = 0; // Сбрасываем счетчик при увеличении
              lastSize = { width, height };
            }
            
            // Проверяем, изменился ли размер (только если это не увеличение)
            if (!isLarger) {
              if (lastSize.width === width && lastSize.height === height) {
                stableSizeCount++;
              } else {
                stableSizeCount = 0;
                lastSize = { width, height };
              }
            }

            // Очищаем предыдущий timeout
            if (resizeTimeout) {
              clearTimeout(resizeTimeout);
            }

            // Используем debounce для установки базового размера после стабилизации
            // Устанавливаем baseSize только если размер не меняется в течение STABLE_DELAY
            // И используем максимальный размер, который мы видели
            resizeTimeout = setTimeout(() => {
              if (baseSize.value.width === 0 || baseSize.value.height === 0) {
                // Используем максимальный размер, который мы видели (это должен быть десктопный размер)
                // Если максимальный размер не установлен, используем текущий размер
                const finalSize = maxSize.width > 0 && maxSize.height > 0 
                  ? maxSize 
                  : { width, height };
                
                // Устанавливаем базовый размер только если он больше текущего (приоритет десктопному размеру)
                // Это гарантирует, что если пользователь настроил координаты на десктопе,
                // базовый размер будет десктопным, даже если страница открыта на мобильном
                if (finalSize.width >= width && finalSize.height >= height) {
                  baseSize.value = finalSize;
                  containerSize.value = { width, height };
                  // Вычисляем масштаб с высокой точностью (4 знака после запятой)
                  // Это обеспечивает точное масштабирование координат
                  const rawScaleX = width / finalSize.width;
                  const rawScaleY = height / finalSize.height;
                  scale.value = {
                    x: Math.round(rawScaleX * 10000) / 10000,
                    y: Math.round(rawScaleY * 10000) / 10000
                  };
                } else {
                  // Если текущий размер больше максимального, используем текущий как базовый
                  baseSize.value = { width, height };
                  containerSize.value = { width, height };
                  scale.value = { x: 1, y: 1 };
                }
                
                if (options.onBaseSizeInitialized) {
                  options.onBaseSizeInitialized(baseSize.value);
                }
              }
            }, STABLE_DELAY); // Ждем STABLE_DELAY мс стабильного размера
          } else {
            // Вычисляем масштаб (как scaleX в TrainerPage: currentWidth / originalImageWidth)
            // При уменьшении контейнера scale < 1, при увеличении scale > 1
            // Используем точные вычисления с округлением до 4 знаков после запятой
            // Это обеспечивает точное масштабирование координат
            const rawScaleX = width / baseSize.value.width;
            const rawScaleY = height / baseSize.value.height;
            const newScale = {
              x: Math.round(rawScaleX * 10000) / 10000,
              y: Math.round(rawScaleY * 10000) / 10000
            };
            
            // Обновляем масштаб только если он изменился более чем на 0.0001 (оптимизация)
            // Это предотвращает лишние пересчеты при незначительных изменениях
            const scaleChanged = Math.abs(scale.value.x - newScale.x) > 0.0001 || 
                                Math.abs(scale.value.y - newScale.y) > 0.0001;
            
            if (scaleChanged) {
              scale.value = newScale;
              
              if (options.onScaleUpdated) {
                options.onScaleUpdated(newScale, containerSize.value);
              }
            }
          }
        }
      }
    });

    resizeObserver.observe(containerRef.value);

    // Пытаемся установить базовый размер сразу
    initializeBaseSize();
    
    // Если размер еще не доступен, используем requestAnimationFrame для ожидания
    if (baseSize.value.width === 0 || baseSize.value.height === 0) {
      requestAnimationFrame(() => {
        initializeBaseSize();
        // Если все еще не установлен, пробуем еще раз через небольшую задержку
        if (baseSize.value.width === 0 || baseSize.value.height === 0) {
          setTimeout(() => {
            initializeBaseSize();
          }, 100);
        }
      });
    }
  };

  // Инициализируем ResizeObserver
  // Используем nextTick для того, чтобы контейнер был уже в DOM
  nextTick(() => {
    setupResizeObserver();
  });

  onUnmounted(() => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
      resizeTimeout = null;
    }
    if (resizeObserver && containerRef.value) {
      resizeObserver.unobserve(containerRef.value);
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  });

  return {
    baseSize,
    containerSize,
    scale
  };
}

